import type { KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import { Option } from "../SelectBase";

interface UseSelectKeyboardParams {
  /**
   * Options in render order — already filtered, and (for MultiSelect) sorted
   * relevant-first. The active index is an offset into this array.
   */
  options: Option[];
  /**
   * Controlled search query. Drives the "Space toggles only when not typing"
   * rule and the Tab commit shortcut.
   */
  search: string;
  /**
   * Whether the popover is open. Reopening resets the active row to the top.
   */
  isOpen: boolean;
  /**
   * Whether the type-to-search Tab-commit shortcut is enabled.
   */
  typeToSearch: boolean;
  /**
   * Skip all handling (e.g. while loading).
   */
  disabled?: boolean;
  /**
   * Scroll the row at `index` into view.
   */
  scrollToIndex: (index: number) => void;
  /**
   * Select the active option — fired on Enter, and on Space when the search
   * box is empty. MultiSelect toggles the option and keeps the popover open;
   * SingleSelect selects it and closes.
   */
  onSelect: (option: Option) => void;
  /**
   * Commit the active option on Tab (type-to-search), before the popover
   * closes. MultiSelect adds it to the selection; SingleSelect sets it.
   */
  onTab: (option: Option) => void;
  /**
   * Close and reset the popover — used by the Tab shortcut.
   */
  onClose: () => void;
}

/**
 * Shared keyboard model for the Single/Multi select popovers. Owns the
 * keyboard-highlighted "active" row and a `handleKeyDown` to attach in the
 * capture phase on the popover content, so it runs before cmdk's own
 * (virtualization-incompatible) navigation and before the search input's
 * default behavior. Only keys it owns stop propagation; everything else
 * (printable characters, Space while typing) falls through to the input.
 */
export function useSelectKeyboard({
  options,
  search,
  isOpen,
  typeToSearch,
  disabled = false,
  scrollToIndex,
  onSelect,
  onTab: onTabCommit,
  onClose
}: UseSelectKeyboardParams) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Reset to the top match whenever the result set changes (open or new query),
  // so Enter/Space act on the top match by default.
  useEffect(() => {
    setActiveIndex(0);
  }, [search, isOpen]);

  function focusIndex(index: number) {
    setActiveIndex(index);
    scrollToIndex(index);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (disabled || options.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(Math.min(activeIndex + 1, options.length - 1));
        return;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(Math.max(activeIndex - 1, 0));
        return;
      case "Home":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(0);
        return;
      case "End":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(options.length - 1);
        return;
      case "Enter": {
        // Select the active row.
        event.preventDefault();
        event.stopPropagation();
        const option = options[activeIndex];
        if (option) onSelect(option);
        return;
      }
      case " ": {
        // Space toggles the active row only when not typing a query, so
        // multi-word search (e.g. "vero moda") still works.
        if (search.length > 0) return;
        event.preventDefault();
        event.stopPropagation();
        const option = options[activeIndex];
        if (option) onSelect(option);
        return;
      }
      case "Tab": {
        // Type-to-search commit shortcut: pick the active match, close, and let
        // focus advance naturally (no preventDefault).
        if (!typeToSearch) return;
        if (search.length > 0) {
          const option = options[activeIndex];
          if (option) onTabCommit(option);
        }
        onClose();
        return;
      }
      default:
        return;
    }
  }

  return {
    activeIndex,
    activeOption: options[activeIndex] as Option | undefined,
    handleKeyDown
  };
}
